/**
 * Fast Track — PWA Service Worker Registration
 * Handles install prompt, update detection, and offline status
 */

// ── Register Service Worker ────────────────────────────────────
export function registerSW() {
  if (!('serviceWorker' in navigator)) {
    console.log('[PWA] Service Worker not supported');
    return;
  }

  // ── JANGAN dipasang saat development ──────────────────────────
  // Service worker menyajikan JS dari cache (StaleWhileRevalidate). Di produksi
  // itu memang tujuannya. Di komputer developer efeknya jebakan: nama file chunk
  // CRA saat `npm start` TIDAK berubah tiap edit, jadi sesudah mengubah kode dan
  // menekan refresh, yang tampil masih versi lama — persis seperti perubahannya
  // tidak berpengaruh. Berjam-jam bisa habis mencari bug yang sudah diperbaiki.
  // Sekalian cabut yang sudah terlanjur terpasang, supaya browser yang sudah
  // pernah membuka localhost tidak tertinggal SW lama selamanya.
  if (process.env.NODE_ENV !== 'production') {
    (async () => {
      const daftar = await navigator.serviceWorker.getRegistrations();
      await Promise.all(daftar.map((r) => r.unregister()));
      // Pembersihan cache SENGAJA di luar syarat "ada registrasi". Service worker
      // yang baru dicabut masih mengendalikan halaman yang sedang terbuka dan bisa
      // membuat ulang cache-nya sampai halaman dimuat ulang — jadi kalau hanya
      // dibersihkan saat pencabutan, sisanya menetap selamanya. Di sini dijalankan
      // tiap kali, sampai benar-benar bersih.
      if (window.caches) {
        const kunci = await caches.keys();
        const buang = kunci.filter((k) => k.startsWith('ft-') || k.startsWith('workbox-'));
        await Promise.all(buang.map((k) => caches.delete(k)));
        if (daftar.length || buang.length) {
          console.log(`[PWA] Mode development — ${daftar.length} service worker dicabut, ${buang.length} cache dibuang.`);
        }
      }
    })().catch(() => {});
    return;
  }

  window.addEventListener('load', async () => {
    try {
      const reg = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/',
        updateViaCache: 'none',
      });

      console.log('[PWA] Service Worker registered:', reg.scope);

      // Check for updates every 60 seconds
      setInterval(() => reg.update(), 60 * 1000);

      // New SW waiting to activate — tampilkan banner update
      reg.addEventListener('updatefound', () => {
        const newSW = reg.installing;
        newSW?.addEventListener('statechange', () => {
          if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
            showUpdateBanner();
          }
        });
      });

    } catch (err) {
      console.error('[PWA] Service Worker registration failed:', err);
    }
  });

  // Reload HANYA setelah user klik tombol Update di banner
  // — TIDAK auto-reload saat controllerchange (mencegah reload loop)
  window.addEventListener('ft-sw-activate', () => {
    window.location.reload();
  });
}

// ── Trigger SW update (dipanggil dari PWAUpdateBanner saat user klik Update) ──
export function activateWaitingSW() {
  navigator.serviceWorker.ready.then((reg) => {
    if (reg.waiting) {
      // Kirim pesan ke SW waiting untuk skipWaiting
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      // Setelah SW aktif, reload
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.dispatchEvent(new CustomEvent('ft-sw-activate'));
      }, { once: true });
    }
  });
}

// ── Show update banner ─────────────────────────────────────────
function showUpdateBanner() {
  window.dispatchEvent(new CustomEvent('ft-sw-update'));
}

// ── Capture install prompt ─────────────────────────────────────
let deferredPrompt = null;

// Ingatan "aplikasi sudah dipasang". Event `appinstalled` hanya menyala SEKALI,
// di tab yang memicu pemasangan. Kalau tidak dicatat, besoknya user membuka
// lewat tab browser biasa (bukan jendela app) dan isInstalled() menjawab false
// — banner install muncul lagi padahal aplikasinya sudah terpasang. Itu persis
// keluhan "sudah di-download tapi popup-nya muncul terus".
const KUNCI_TERPASANG = 'ft-pwa-terpasang';

function tandaiTerpasang() {
  try {
    localStorage.setItem(KUNCI_TERPASANG, 'true');
  } catch (e) {
    /* localStorage bisa diblokir (mode penyamaran) — abaikan saja */
  }
}

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredPrompt = e;
  console.log('[PWA] Install prompt captured');
  window.dispatchEvent(new CustomEvent('ft-installable'));
});

window.addEventListener('appinstalled', () => {
  deferredPrompt = null;
  tandaiTerpasang();
  console.log('[PWA] App installed successfully!');
  window.dispatchEvent(new CustomEvent('ft-installed'));
});

// Apakah browser sedang menyimpan prompt yang siap dipakai. Dipakai komponen
// banner: `beforeinstallprompt` bisa menyala sebelum React sempat memasang
// listener-nya, dan event yang lewat tidak pernah menyala ulang.
export function isInstallable() {
  return deferredPrompt !== null;
}

export function triggerInstall() {
  if (!deferredPrompt) return false;
  // Event beforeinstallprompt sekali pakai: sesudah prompt() dipanggil, event
  // yang sama tidak bisa dipakai lagi. Dikosongkan DI SINI (bukan di dalam
  // .then) supaya klik kedua saat dialog masih terbuka tidak melempar error.
  const prompt = deferredPrompt;
  deferredPrompt = null;
  prompt.prompt();
  prompt.userChoice
    .then((choice) => {
      if (choice.outcome === 'accepted') tandaiTerpasang();
    })
    .catch(() => {});
  return true;
}

export function isInstalled() {
  // Semua mode yang berarti "berjalan sebagai aplikasi, bukan tab browser".
  // window-controls-overlay ikut karena manifest menaruhnya paling depan di
  // display_override, jadi di desktop jendela app-nya TIDAK match 'standalone'.
  const modeApp = ['standalone', 'minimal-ui', 'fullscreen', 'window-controls-overlay'].some(
    (mode) => window.matchMedia(`(display-mode: ${mode})`).matches
  );
  let tercatat = false;
  try {
    tercatat = localStorage.getItem(KUNCI_TERPASANG) === 'true';
  } catch (e) {
    /* diabaikan */
  }
  return modeApp || window.navigator.standalone === true || tercatat;
}

export function isOnline() {
  return navigator.onLine;
}
